import { useQuery } from '@tanstack/react-query'
import React, { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Roles } from '../auth/rbac'
import { useAuth } from '../auth/AuthContext'
import { api } from '../lib/api'
import { asCollection } from '../lib/collections'
import { useToast } from '../ui/ToastProvider'
import ListContainer from '../ui/ListContainer'
import MenuGuideButton from '../components/MenuGuideButton'

const CATEGORY_LABELS = {
    INSURANCE: '보험',
    ECONOMY: '경제동향',
    REAL_ESTATE: '부동산',
    TAX: '세금',
    SECURITIES: '증권/투자',
    BANKING: '은행/금리',
    FINANCIAL_PRODUCT: '금융상품',
    MANAGEMENT: '경영',
    ETC: '기타'
}

// 카테고리별 색상 스타일 (배경색, 텍스트 색상)
const CATEGORY_STYLES = {
    INSURANCE: 'bg-blue-100 text-blue-700',
    ECONOMY: 'bg-green-100 text-green-700',
    REAL_ESTATE: 'bg-purple-100 text-purple-700',
    TAX: 'bg-red-100 text-red-700',
    SECURITIES: 'bg-yellow-100 text-yellow-700',
    BANKING: 'bg-indigo-100 text-indigo-700',
    FINANCIAL_PRODUCT: 'bg-pink-100 text-pink-700',
    MANAGEMENT: 'bg-teal-100 text-teal-700',
    ETC: 'bg-slate-100 text-slate-700'
}

const PUBLICATION_TABS = [
    { value: 'PUBLISHED', label: '게시됨' },
    { value: 'PENDING', label: '승인 대기' },
    { value: 'REJECTED', label: '반려' },
]

export default function InsuranceNewsPage({ embedded = false }) {
    const { hasAnyRole } = useAuth()
    const { show } = useToast()
    const [q, setQ] = useState('')
    const [category, setCategory] = useState('')
    const [searchParams, setSearchParams] = useSearchParams()
    const [page, setPage] = useState(Number(searchParams.get('page') || '1'))
    const [size] = useState(Number(searchParams.get('size') || '20'))
    const [pubFilter, setPubFilter] = useState(() => searchParams.get('publicationStatus') || 'PUBLISHED')
    const [items, setItems] = useState([])
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState(null)
    const [total, setTotal] = useState(0)
    const currentSearch = searchParams.toString()
    const embeddedTab = embedded ? searchParams.get('tab') : null

    // OPERATOR 역할 체크
    const isOperator = hasAnyRole ? hasAnyRole([Roles.OPERATOR]) : false

    // URL 동기화
    useEffect(() => {
        const next = new URLSearchParams()
        if (embedded && embeddedTab) next.set('tab', embeddedTab)
        if (q) next.set('q', q)
        if (category) next.set('category', category)
        if (page && page !== 1) next.set('page', String(page))
        if (size && size !== 20) next.set('size', String(size))
        if (isOperator && pubFilter && pubFilter !== 'PUBLISHED') {
            next.set('publicationStatus', pubFilter)
        }
        if (next.toString() !== currentSearch) {
            setSearchParams(next, { replace: true })
        }
    }, [q, category, page, size, pubFilter, isOperator, embedded, embeddedTab, currentSearch, setSearchParams])

    const showDigestBanner = !isOperator || pubFilter === 'PUBLISHED'

    const digestLatestQuery = useQuery({
        queryKey: ['insurance-news-daily-digests-latest'],
        queryFn: async () => {
            const res = await api.get('/insurance-news/daily-digests/latest-published')
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            return payload && typeof payload === 'object' && payload.id != null ? payload : null
        },
        enabled: showDigestBanner,
    })

    const digestPendingQuery = useQuery({
        queryKey: ['insurance-news-daily-digests-pending'],
        queryFn: async () => {
            const res = await api.get('/insurance-news/daily-digests', {
                params: { publicationStatus: 'PENDING', page: 0, size: 20, sort: 'createdAt,DESC' },
            })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const raw = Array.isArray(payload) ? payload : (payload?.content || [])
            return Array.isArray(raw) ? raw : []
        },
        enabled: isOperator,
    })

    const listQuery = useQuery({
        queryKey: ['insurance-news', { q, category, page, size, pubFilter: isOperator ? pubFilter : null }],
        queryFn: async () => {
            const params = {}
            if (q) params.q = q
            if (category) params.category = category
            if (isOperator) {
                params.publicationStatus = pubFilter
            }
            params.page = page - 1
            params.size = size
            params.sort = 'createdAt,DESC'
            const res = await api.get('/insurance-news', { params })
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            // Page 객체인 경우 content를 사용, 아니면 items 또는 배열 자체
            const raw = asCollection(payload)
            const totalElements = Number(payload?.totalElements ?? payload?.total ?? raw.length ?? 0)
            return { items: raw, total: totalElements }
        },
        keepPreviousData: true,
    })

    useEffect(() => {
        const data = listQuery.data?.items || []
        setItems(data)
        setTotal(listQuery.data?.total || 0)
        setLoading(listQuery.isFetching)
        setError(listQuery.isError ? '목록을 불러오지 못했습니다' : null)
    }, [listQuery.data, listQuery.isFetching, listQuery.isError])

    const handleDelete = async (e, newsId) => {
        e.stopPropagation()
        if (!window.confirm('정말 삭제하시겠습니까?')) return
        try {
            await api.delete(`/insurance-news/${newsId}`)
            show('삭제되었습니다', 'success')
            listQuery.refetch()
        } catch (err) {
            show('삭제에 실패했습니다', 'error')
        }
    }

    const handleApprove = async (e, newsId) => {
        e.stopPropagation()
        if (!window.confirm('이 뉴스를 사용자에게 게시할까요?')) return
        try {
            await api.post(`/insurance-news/${newsId}/approve`)
            show('게시되었습니다', 'success')
            listQuery.refetch()
        } catch (err) {
            show(err?.response?.data?.message || '승인에 실패했습니다', 'error')
        }
    }

    const handleReject = async (e, newsId) => {
        e.stopPropagation()
        if (!window.confirm('이 뉴스를 반려 처리할까요?')) return
        try {
            await api.post(`/insurance-news/${newsId}/reject`)
            show('반려되었습니다', 'success')
            listQuery.refetch()
        } catch (err) {
            show(err?.response?.data?.message || '반려에 실패했습니다', 'error')
        }
    }

    const handleDigestApprove = async (e, digestId) => {
        e.preventDefault()
        e.stopPropagation()
        if (!window.confirm('이 다이제스트를 게시할까요?')) return
        try {
            await api.post(`/insurance-news/daily-digests/${digestId}/approve`)
            show('다이제스트가 게시되었습니다', 'success')
            digestPendingQuery.refetch()
            digestLatestQuery.refetch()
        } catch (err) {
            show(err?.response?.data?.message || '승인에 실패했습니다', 'error')
        }
    }

    const handleDigestReject = async (e, digestId) => {
        e.preventDefault()
        e.stopPropagation()
        if (!window.confirm('이 다이제스트를 반려할까요?')) return
        try {
            await api.post(`/insurance-news/daily-digests/${digestId}/reject`)
            show('반려되었습니다', 'success')
            digestPendingQuery.refetch()
        } catch (err) {
            show(err?.response?.data?.message || '반려에 실패했습니다', 'error')
        }
    }

    const handleRunDigestCuration = async () => {
        if (!window.confirm('RSS에서 어제·오늘 구간으로 다이제스트 초안을 생성합니다. 해당 일자에 이미 승인 대기 또는 게시된 다이제스트가 있으면 건너뜁니다. 진행할까요?')) return
        try {
            const res = await api.post('/insurance-news/daily-digests/curate', {})
            const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
            const n = Number(payload?.createdDigestDays ?? 0)
            show(`큐레이션 완료: 새로 만든 일자 ${n}건`, 'success')
            digestPendingQuery.refetch()
            digestLatestQuery.refetch()
        } catch (err) {
            show(err?.response?.data?.message || '큐레이션에 실패했습니다', 'error')
        }
    }

    const canShareNews = (news) => (news.publicationStatus || 'PUBLISHED') === 'PUBLISHED'

    const handleShareKakao = (e, news) => {
        e.stopPropagation()
        if (!canShareNews(news)) {
            show('게시된 뉴스만 공유할 수 있습니다', 'error')
            return
        }
        if (!news.externalUrl) {
            show('공유할 링크가 없습니다', 'error')
            return
        }
        // 카카오톡 공유 (Kakao SDK 사용)
        if (window.Kakao && window.Kakao.isInitialized()) {
            window.Kakao.Share.sendDefault({
                objectType: 'feed',
                content: {
                    title: news.title,
                    description: news.summary || news.title,
                    imageUrl: '', // 필요시 이미지 URL 추가
                    link: {
                        mobileWebUrl: news.externalUrl,
                        webUrl: news.externalUrl,
                    },
                },
            })
        } else {
            // Kakao SDK가 없으면 링크 복사
            navigator.clipboard.writeText(news.externalUrl).then(() => {
                show('링크가 복사되었습니다', 'success')
            }).catch(() => {
                show('링크 복사에 실패했습니다', 'error')
            })
        }
    }

    const handleShareSms = (e, news) => {
        e.stopPropagation()
        if (!canShareNews(news)) {
            show('게시된 뉴스만 공유할 수 있습니다', 'error')
            return
        }
        if (!news.externalUrl) {
            show('공유할 링크가 없습니다', 'error')
            return
        }
        const text = `${news.title}\n${news.externalUrl}`
        const smsUrl = `sms:?body=${encodeURIComponent(text)}`
        window.location.href = smsUrl
    }

    const handleShareLink = (e, news) => {
        e.stopPropagation()
        if (!canShareNews(news)) {
            show('게시된 뉴스만 공유할 수 있습니다', 'error')
            return
        }
        if (!news.externalUrl) {
            show('공유할 링크가 없습니다', 'error')
            return
        }
        navigator.clipboard.writeText(news.externalUrl).then(() => {
            show('링크가 복사되었습니다', 'success')
        }).catch(() => {
            show('링크 복사에 실패했습니다', 'error')
        })
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        const d = new Date(dateString)
        if (isNaN(d.getTime())) return String(dateString)
        const y = d.getFullYear()
        const mm = String(d.getMonth() + 1).padStart(2, '0')
        const dd = String(d.getDate()).padStart(2, '0')
        return `${y}-${mm}-${dd}`
    }

    const header = (
        <div className="p-4 border-b bg-white">
            {(!embedded || isOperator) && (
                <div className="flex items-center justify-between mb-4">
                    {!embedded ? (
                        <div>
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-bold">보험뉴스</h1>
                                <MenuGuideButton slug="insurance-news" title="업무지원 가이드" />
                            </div>
                        </div>
                    ) : (
                        <div className="min-w-0" />
                    )}
                    {isOperator && (
                        <div className="flex flex-wrap items-center gap-2">
                            <button
                                type="button"
                                onClick={() => void handleRunDigestCuration()}
                                className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
                            >
                                어제~오늘 큐레이션
                            </button>
                            <Link
                                to="/work/insurance-news/daily-digest/new"
                                className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-900 hover:bg-amber-100"
                            >
                                다이제스트
                            </Link>
                            <Link
                                to="/work/insurance-news/new"
                                className="inline-flex items-center gap-2 rounded-lg bg-brand-sky px-4 py-2 text-sm font-semibold text-white hover:bg-brand-sky/90"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4">
                                    <path d="M10.75 4.75a.75.75 0 0 0-1.5 0v4.5h-4.5a.75.75 0 0 0 0 1.5h4.5v4.5a.75.75 0 0 0 1.5 0v-4.5h4.5a.75.75 0 0 0 0-1.5h-4.5v-4.5Z" />
                                </svg>
                                작성
                            </Link>
                        </div>
                    )}
                </div>
            )}
            {isOperator && (
                <div className="flex flex-wrap gap-2 mb-3">
                    {PUBLICATION_TABS.map((t) => (
                        <button
                            key={t.value}
                            type="button"
                            onClick={() => {
                                setPubFilter(t.value)
                                setPage(1)
                            }}
                            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                                pubFilter === t.value
                                    ? 'border-brand-sky bg-brand-sky/10 text-brand-sky'
                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex flex-row items-center gap-2">
                <select
                    value={category}
                    onChange={(e) => {
                        setCategory(e.target.value)
                        setPage(1)
                    }}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                >
                    <option value="">전체 카테고리</option>
                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                    ))}
                </select>
                <div className="flex-1">
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="검색할 단어 및 제목을 입력하세요"
                        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-sky focus:outline-none focus:ring-2 focus:ring-brand-sky/20"
                    />
                </div>
            </div>
        </div>
    )

    // 모바일 카드 형태 (보케어 스타일)
    const mobileCards = items.length > 0 ? (
        <div className="md:hidden space-y-4 p-4">
            {items.map((news) => (
                <div
                    key={news.id}
                    className="border-b border-slate-200 pb-4 last:border-b-0 last:pb-0"
                >
                    <div className="flex items-start justify-between gap-2 mb-2">
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                                <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium whitespace-nowrap ${CATEGORY_STYLES[news.category] || CATEGORY_STYLES.ETC}`}>
                                    {CATEGORY_LABELS[news.category] || news.category}
                                </span>
                                {isOperator && news.publicationStatus === 'PENDING' && (
                                    <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">승인 대기</span>
                                )}
                                {isOperator && news.publicationStatus === 'REJECTED' && (
                                    <span className="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">반려</span>
                                )}
                                <span className="text-xs text-slate-500 whitespace-nowrap">
                                    {news.createdAt ? formatDate(news.createdAt) : '-'}
                                </span>
                            </div>
                            <div 
                                className="font-bold text-base text-brand-sky cursor-pointer hover:underline mb-1" 
                                onClick={(e) => {
                                    e.stopPropagation()
                                    if (news.externalUrl) {
                                        window.open(news.externalUrl, '_blank', 'noopener,noreferrer')
                                    }
                                }}
                            >
                                {news.title || '(제목 없음)'}
                            </div>
                            {news.summary && (
                                <div className="text-sm text-slate-600 line-clamp-2 mt-1">{news.summary}</div>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center justify-between mt-3">
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                disabled={!canShareNews(news)}
                                onClick={(e) => handleShareKakao(e, news)}
                                className="inline-flex items-center justify-center rounded bg-[#FEE500] px-3 py-1.5 text-xs font-medium hover:bg-[#FEE500]/90 disabled:cursor-not-allowed disabled:opacity-40"
                                title="카카오톡 공유"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4 mr-1">
                                    <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3Z"/>
                                </svg>
                                카톡공유
                            </button>
                            <button
                                type="button"
                                disabled={!canShareNews(news)}
                                onClick={(e) => handleShareSms(e, news)}
                                className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-2 py-1.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                                title="문자 공유"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                                    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                                </svg>
                            </button>
                            <button
                                type="button"
                                disabled={!canShareNews(news)}
                                onClick={(e) => handleShareLink(e, news)}
                                className="inline-flex items-center justify-center rounded border border-slate-300 bg-white px-2 py-1.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                                title="링크 복사"
                            >
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                </svg>
                            </button>
                        </div>
                        {isOperator && (
                            <div className="flex flex-wrap items-center justify-end gap-2">
                                {news.publicationStatus === 'PENDING' && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={(e) => handleApprove(e, news.id)}
                                            className="text-xs font-medium text-emerald-700 hover:underline"
                                        >
                                            승인
                                        </button>
                                        <button
                                            type="button"
                                            onClick={(e) => handleReject(e, news.id)}
                                            className="text-xs text-slate-600 hover:underline"
                                        >
                                            반려
                                        </button>
                                    </>
                                )}
                                <Link
                                    to={`/work/insurance-news/${news.id}/edit`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="text-xs text-brand-sky hover:underline"
                                >
                                    수정
                                </Link>
                                <button
                                    type="button"
                                    onClick={(e) => handleDelete(e, news.id)}
                                    className="text-xs text-red-600 hover:underline"
                                >
                                    삭제
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ))}
        </div>
    ) : null

    // PC 테이블 형태
    const table = items.length > 0 ? (
        <div className="hidden md:block">
            <table className="min-w-full text-sm table-fixed">
                <colgroup>
                    <col className="w-24" />
                    <col className="w-auto" />
                    <col className="w-28" />
                    <col className="w-32" />
                    {isOperator && <col className="w-44" />}
                </colgroup>
                <thead className="bg-slate-50 text-slate-600">
                    <tr>
                        <th className="px-4 py-3 text-left">카테고리</th>
                        <th className="px-4 py-3 text-left">뉴스</th>
                        <th className="px-4 py-3 text-left">날짜</th>
                        <th className="px-4 py-3 text-center">공유</th>
                        {isOperator && (
                            <th className="px-4 py-3 text-right">관리</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {items.map((news) => (
                        <tr
                            key={news.id}
                            className="hover:bg-slate-50 border-b"
                        >
                            <td className="px-4 py-3">
                                <div className="flex flex-col gap-1 items-start">
                                    <span className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium whitespace-nowrap ${CATEGORY_STYLES[news.category] || CATEGORY_STYLES.ETC}`}>
                                        {CATEGORY_LABELS[news.category] || news.category}
                                    </span>
                                    {isOperator && news.publicationStatus === 'PENDING' && (
                                        <span className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">승인 대기</span>
                                    )}
                                    {isOperator && news.publicationStatus === 'REJECTED' && (
                                        <span className="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700">반려</span>
                                    )}
                                </div>
                            </td>
                            <td className="px-4 py-3">
                                <div 
                                    className="font-bold text-base text-slate-900 truncate cursor-pointer hover:text-brand-sky hover:underline" 
                                    title={news.title || '(제목 없음)'}
                                    onClick={(e) => {
                                        e.stopPropagation()
                                        if (news.externalUrl) {
                                            window.open(news.externalUrl, '_blank', 'noopener,noreferrer')
                                        }
                                    }}
                                >
                                    {news.title || '(제목 없음)'}
                                </div>
                                {news.summary && (
                                    <div className="mt-1 text-sm text-slate-600 line-clamp-2">{news.summary}</div>
                                )}
                            </td>
                            <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                                {news.createdAt ? formatDate(news.createdAt) : '-'}
                            </td>
                            <td className="px-4 py-3">
                                <div className="flex items-center justify-center gap-2">
                                    <button
                                        type="button"
                                        disabled={!canShareNews(news)}
                                        onClick={(e) => handleShareKakao(e, news)}
                                        className="inline-flex items-center justify-center rounded bg-[#FEE500] p-1.5 hover:bg-[#FEE500]/90 disabled:cursor-not-allowed disabled:opacity-40"
                                        title="카카오톡 공유"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-4 w-4">
                                            <path d="M12 3c5.799 0 10.5 3.664 10.5 8.185 0 4.52-4.701 8.184-10.5 8.184a13.5 13.5 0 0 1-1.727-.11l-4.408 2.883c-.501.265-.678.236-.472-.413l.892-3.678c-2.88-1.46-4.785-3.99-4.785-6.866C1.5 6.665 6.201 3 12 3Z"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!canShareNews(news)}
                                        onClick={(e) => handleShareSms(e, news)}
                                        className="inline-flex items-center justify-center rounded border border-slate-300 bg-white p-1.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        title="문자 공유"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                                            <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"/>
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        disabled={!canShareNews(news)}
                                        onClick={(e) => handleShareLink(e, news)}
                                        className="inline-flex items-center justify-center rounded border border-slate-300 bg-white p-1.5 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                                        title="링크 복사"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
                                            <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                                            <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                                        </svg>
                                    </button>
                                </div>
                            </td>
                            {isOperator && (
                                <td className="px-4 py-3 text-right">
                                    <div className="flex flex-wrap items-center justify-end gap-x-2 gap-y-1">
                                        {news.publicationStatus === 'PENDING' && (
                                            <>
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleApprove(e, news.id)}
                                                    className="text-xs font-medium text-emerald-700 hover:underline"
                                                >
                                                    승인
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={(e) => handleReject(e, news.id)}
                                                    className="text-xs text-slate-600 hover:underline"
                                                >
                                                    반려
                                                </button>
                                            </>
                                        )}
                                        <Link
                                            to={`/work/insurance-news/${news.id}/edit`}
                                            onClick={(e) => e.stopPropagation()}
                                            className="text-xs text-brand-sky hover:underline"
                                        >
                                            수정
                                        </Link>
                                        <button
                                            type="button"
                                            onClick={(e) => handleDelete(e, news.id)}
                                            className="text-xs text-red-600 hover:underline"
                                        >
                                            삭제
                                        </button>
                                    </div>
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    ) : null

    const latestDigest = digestLatestQuery.data
    const pendingDigests = digestPendingQuery.data || []

    const digestLatestBanner =
        showDigestBanner && latestDigest ? (
            <div className="border-b border-amber-200 bg-amber-50/90 px-4 py-4 md:px-6">
                <div className="mb-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex rounded-full bg-amber-200 px-2 py-0.5 text-xs font-semibold text-amber-950">
                        오늘의 보험 뉴스
                    </span>
                    {latestDigest.digestDate ? (
                        <span className="text-xs text-slate-600">{String(latestDigest.digestDate)}</span>
                    ) : null}
                </div>
                <h2 className="mb-2 text-lg font-bold text-slate-900">{latestDigest.title}</h2>
                <div className="max-h-[min(60vh,520px)] overflow-y-auto whitespace-pre-wrap text-sm text-slate-800">
                    {latestDigest.content}
                </div>
            </div>
        ) : null

    const digestPendingPanel =
        isOperator && pendingDigests.length > 0 ? (
            <div className="border-b border-slate-200 bg-slate-50 px-4 py-3 md:px-6">
                <div className="mb-2 text-xs font-semibold text-slate-600">승인 대기 다이제스트</div>
                <ul className="space-y-2">
                    {pendingDigests.map((d) => (
                        <li
                            key={d.id}
                            className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200/80 pb-2 text-sm last:border-b-0 last:pb-0"
                        >
                            <span className="text-slate-800">
                                {d.title}{' '}
                                <span className="text-slate-500">({d.digestDate != null ? String(d.digestDate) : '-'})</span>
                            </span>
                            <div className="flex flex-wrap gap-2">
                                <Link
                                    to={`/work/insurance-news/daily-digest/${d.id}/edit`}
                                    className="text-xs font-medium text-brand-sky hover:underline"
                                >
                                    수정
                                </Link>
                                <button
                                    type="button"
                                    className="text-xs font-medium text-emerald-700 hover:underline"
                                    onClick={(e) => handleDigestApprove(e, d.id)}
                                >
                                    승인
                                </button>
                                <button
                                    type="button"
                                    className="text-xs text-slate-600 hover:underline"
                                    onClick={(e) => handleDigestReject(e, d.id)}
                                >
                                    반려
                                </button>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>
        ) : null

    const mainNewsBlock =
        items.length > 0 ? (
            <>
                {mobileCards}
                {table}
            </>
        ) : null

    const composedDigestAndNews = (
        <>
            {digestLatestBanner}
            {digestPendingPanel}
            {mainNewsBlock}
        </>
    )

    const hasDigestOrNews = Boolean(digestLatestBanner || digestPendingPanel || mainNewsBlock)

    const footer = total > 0 ? (
        <div className="p-4 border-t bg-white flex items-center justify-between">
            <div className="text-sm text-slate-600">
                총 {total}건 (페이지 {page} / {Math.ceil(total / size)})
            </div>
            <div className="flex gap-2">
                <button
                    type="button"
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    className="rounded-lg border border-slate-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                    이전
                </button>
                <button
                    type="button"
                    onClick={() => setPage(p => Math.min(Math.ceil(total / size), p + 1))}
                    disabled={page >= Math.ceil(total / size)}
                    className="rounded-lg border border-slate-300 px-3 py-1 text-sm disabled:opacity-50"
                >
                    다음
                </button>
            </div>
        </div>
    ) : null

    return (
        <ListContainer
            loading={loading}
            error={error}
            onRetry={() => listQuery.refetch()}
            header={header}
            table={hasDigestOrNews ? composedDigestAndNews : null}
            footer={footer}
            emptyText="보험뉴스가 없습니다"
        />
    )
}
